import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { PERMISSION_LEVEL } from "../redux/reducer/permissionsSlice";
import { fetchAppPermissions } from "../redux/actions/permissionsActions";

/**
 * Checks an app permission action (e.g. feed_community.comment).
 * Returns helpers to gate UI and show PermissionPopupModal when level is "popup".
 */
export function useAppPermission(actionSlug) {
  const dispatch = useDispatch();
  const action = useSelector(
    (state) => state.permissions?.actionsBySlug?.[actionSlug],
  );
  const isLoading = useSelector((state) => state.permissions?.isLoading);
  const isLoaded = useSelector((state) => state.permissions?.isLoaded);

  const [isModalOpen, setIsModalOpen] = useState(false);

  // Defensive self-heal: if a permission-consuming component mounts
  // before the boot fetch has run (or after a logout that cleared the
  // slice), kick off the fetch here. The thunk dedupes on isLoading
  // so multiple consumers won't pile up duplicate requests.
  useEffect(() => {
    if (!isLoaded && !isLoading) {
      dispatch(fetchAppPermissions());
    }
  }, [isLoaded, isLoading, dispatch]);

  const level = isLoaded
    ? action?.level ?? PERMISSION_LEVEL.COMPLETE
    : null;
  const popupText = action?.popup_text ?? null;
  const popupTitle = action?.popup_title ?? null;
  // Whether the backend actually sent this action. An unknown slug reads as
  // COMPLETE above so a missing action never blocks anything — but that same
  // default must not be read as permission to *open* something that is
  // otherwise closed, or a payload without the action would hand out paid
  // content. Callers granting access off a permission check this first.
  const isConfigured = Boolean(action);

  const checkPermission = useCallback(() => {
    if (!isLoaded) {
      return { allowed: false, reason: isLoading ? "loading" : "unknown" };
    }

    if (level === PERMISSION_LEVEL.COMPLETE) {
      return { allowed: true };
    }

    if (level === PERMISSION_LEVEL.POPUP) {
      setIsModalOpen(true);
      return { allowed: false, reason: "popup" };
    }

    return { allowed: false, reason: level };
  }, [isLoaded, isLoading, level]);

  const runIfAllowed = useCallback(
    (onAllowed) => {
      const result = checkPermission();
      // Treat "loading" as pass-through: permissions haven't been fetched
      // yet (boot window or post-resume refetch), so we don't know whether
      // to allow or deny. Silently dropping the click here is what made the
      // Community tab feel intermittently unresponsive. The destination
      // page re-checks permissions on mount, so gating is preserved —
      // we're only losing the silent-drop UX, not the gate itself.
      const shouldRun = result.allowed || result.reason === "loading";
      if (shouldRun && typeof onAllowed === "function") {
        onAllowed();
      }
      return result;
    },
    [checkPermission],
  );

  const isBlocked =
    isLoaded &&
    (level === PERMISSION_LEVEL.BLOCKED || level === PERMISSION_LEVEL.LIMITED);

  return {
    level,
    isConfigured,
    popupText,
    popupTitle,
    isModalOpen,
    setIsModalOpen,
    checkPermission,
    runIfAllowed,
    isBlocked,
    isLoading,
    isLoaded,
  };
}
